<template>
  <div class="relative w-full flex flex-col border border-border-normal" style="border-radius: 18px;">
    <PreviewLockedOverlay v-if="locked" class="absolute inset-0 z-10" style="border-radius: 18px;" show-button-icon
    :title="`Start Tracking ${brand?.name || 'this brand'} to Unlock Rank Distribution`"
    description="Ad Rank is available only for the brands you are tracking." />
    <!-- Header -->
    <div class="w-full flex items-center p-3">
      <!-- Title -->
      <div class="flex items-center gap-1 p-1">
        <ScatterGraphIcon class="text-icon-muted" />
        <BaseText type="label" size="sm" class="text-text-muted">
          Top 100
        </BaseText>
      </div>
      <!-- Info Label -->
      <button class="group ml-1 flex items-center p-1"
      @click="$emit('show-info-modal')">
        <InfoIcon class="text-text-subdued transition-colors duration-100 group-hover:text-text-normal" />
        <BaseText type="label" size="sm" class="text-text-normal px-1 transition-colors duration-100 group-hover:text-text-muted">
          How is rank determined?
        </BaseText>
      </button>
      <!-- Date Selector -->
      <div class="relative ml-auto">
        <button class="flex items-center gap-1 px-1.5 py-1 rounded-md transition-colors duration-100"
        :class="showDateSelector ? 'bg-neutral-25' : 'hover:bg-neutral-25'"
        @click="showDateSelector = !showDateSelector">
          <CalendarIcon class="text-icon-normal" />
          <BaseText type="label" size="sm" class="text-text-muted">
            {{ dateFilterLabel }}
          </BaseText>
        </button>
        <transition>
          <div v-if="showDateSelector" class="date-selector-dropdown" v-on-clickaway="() => showDateSelector = false">
            <DateRangeSelectorDropdown
              single-date
              :selected-date.sync="selectedDate"
              close-on-apply
              info-label="The chart will show ad rankings at this date"
              @close="showDateSelector = false"
            />
          </div>
        </transition>
      </div>
    </div>
    <!-- Graph Area -->
    <div class="w-full mb-3" style="height: 332px;">
      <AdRankScatterChart
        :scatter-ads="scatterAds"
        :loading="loading"
        @show-ad-details="showAdvertisementDetails"
      />
    </div>
    <!-- Advertisement Details Drawer -->
    <AdvertisementDetailsDrawer
      v-if="showAdvertisementDetailsDrawer"
      :advertisement="selectedAdvertisement"
      discovery
      @close="showAdvertisementDetailsDrawer = false"
    />
  </div>
</template>

<script>
import ForeplayAPI from '@/api/foreplayServer'
import moment from 'moment'
import { mixin as clickaway } from 'vue-clickaway2'
import { getAdRankScatterMockData } from '@/utils/spyder/mockDataUtils'

// Components
import AdRankScatterChart from './AdRankScatterChart.vue'
import AdvertisementDetailsDrawer from '@/components/advertisements/AdvertisementDetailsDrawer.vue'
import DateRangeSelectorDropdown from '@/components/search/TopBarComponents/DateRangeSelectorDropdown.vue'
import PreviewLockedOverlay from '@/components/spyderV2/PreviewLockedOverlay.vue'

// Icons
import ScatterGraphIcon from '@/components/globals/Icons/LensIcons/graphs/ScatterGraphIcon.vue'
import InfoIcon from '@/components/globals/Icons/InfoSimpleIcon.vue'
import CalendarIcon from '@/components/globals/Icons/CalendarIcon.vue'

export default {
  name: 'AdRankingGraph',
  mixins: [clickaway],
  components: {
    AdRankScatterChart,
    AdvertisementDetailsDrawer,
    DateRangeSelectorDropdown,
    PreviewLockedOverlay,
    ScatterGraphIcon,
    InfoIcon,
    CalendarIcon
  },
  props: {
    brand: {
      type: Object
    },
    pageId: {
      type: String,
      default: null
    }
  },
  data () {
    return {
      scatterAds: [],
      loading: true,
      showDateSelector: false,
      selectedDate: null,
      showAdvertisementDetailsDrawer: false,
      selectedAdvertisement: null,
      latestRequestId: 0
    }
  },
  watch: {
    // Watch both request identifiers together so a brand switch only triggers a single fetch
    rankingsRequestKey: {
      handler () {
        // Drop the previous advertiser's points right away so the chart never plots them against the new one
        this.scatterAds = []
        this.fetchScatterData()
      },
      immediate: true
    },
    selectedDate () {
      this.fetchScatterData()
    }
  },
  computed: {
    locked () {
      const isSubscribed = this.brand?.is_subscribed
      return isSubscribed === undefined ? false : !isSubscribed
    },
    rankingsRequestKey () {
      return `${this.brand?.id}-${this.pageId}`
    },
    dateFilterLabel () {
      if (!this.selectedDate) return moment().format('MMMM Do, YYYY')
      return moment(this.selectedDate).format('MMMM Do, YYYY')
    }
  },
  methods: {
    async fetchScatterData () {
      // Any in-flight request is now stale, since the advertiser or date it was made for has changed
      const requestId = ++this.latestRequestId
      this.loading = true
      // The brand is still resolving upstream, so hold the loading state rather than flashing an empty chart
      if (!this.brand?.id) return
      // If the brand is locked, use the mock data
      if (!this.brand?.is_subscribed) {
        this.scatterAds = getAdRankScatterMockData()
        this.loading = false
        return
      }
      // Rankings are scoped to a single Meta advertiser, so we can't fetch them for brands without one
      if (!this.pageId) {
        this.scatterAds = []
        this.loading = false
        return
      }
      try {
        const date = this.selectedDate ? moment(this.selectedDate).format('YYYY-MM-DD') : moment().format('YYYY-MM-DD')
        const { data } = await ForeplayAPI.Brands.getBrandRankingsScatterData(this.brand.id, this.pageId, date, 7)
        if (requestId !== this.latestRequestId) return
        this.scatterAds = data ?? []
      } catch (error) {
        if (requestId !== this.latestRequestId) return
        console.error('Error fetching scatter data:', error)
        this.$showAlert({
          type: 'error',
          message: 'Failed to fetch scatter data. See console for details.'
        })
      } finally {
        if (requestId === this.latestRequestId) this.loading = false
      }
    },
    showAdvertisementDetails (advertisement) {
      if (this.locked) return
      this.selectedAdvertisement = advertisement
      this.showAdvertisementDetailsDrawer = true
    }
  }
}
</script>

<style scoped>
.date-selector-dropdown {
  position: absolute;
  top: calc(100% + 6px);
  right: 0;
  z-index: 500;
}

.v-enter-active, .v-leave-active {
  transition: opacity 100ms ease-in-out;
}
.v-enter-from, .v-enter, .v-leave-to {
  opacity: 0;
}
.v-enter-to, .v-leave-from {
  opacity: 1;
}
</style>